// Client-side deck text extraction — keeps parsing out of the Worker. PDFs go
// through pdf.js; PPTX (a ZIP of slide XML) is unzipped and its text runs pulled.
// The extracted text is sent to /api/extract-deal for field extraction.
import * as pdfjsLib from 'pdfjs-dist';
import { unzipSync } from 'fflate';
// Vite bundles the worker as a same-origin asset (CSP-safe on Pages).
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { decodeEntities } from './xml.js';

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

/** Extract concatenated text from all pages of a PDF. */
export async function extractPdfText(file: File): Promise<string> {
  const data = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data }).promise;
  const parts: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    parts.push(content.items.map((it) => ('str' in it ? it.str : '')).join(' '));
  }
  return parts.join('\n');
}

/** Extract text from a .pptx (Office Open XML) — unzip and pull <a:t> runs per slide. */
export async function extractPptxText(file: File): Promise<string> {
  const buf = new Uint8Array(await file.arrayBuffer());
  const files = unzipSync(buf);
  const dec = new TextDecoder();
  const slideKeys = Object.keys(files)
    .filter((k) => /^ppt\/slides\/slide\d+\.xml$/.test(k))
    .sort((a, b) => Number(a.match(/(\d+)/)?.[1] ?? 0) - Number(b.match(/(\d+)/)?.[1] ?? 0));
  const parts: string[] = [];
  for (const k of slideKeys) {
    const xml = dec.decode(files[k]);
    const texts = [...xml.matchAll(/<a:t>(.*?)<\/a:t>/g)].map((m) => decodeEntities(m[1] ?? ''));
    if (texts.length) parts.push(texts.join(' '));
  }
  return parts.join('\n');
}

/** Route a deck file to the right extractor by type/extension. */
export async function extractDeckText(file: File): Promise<string> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.pptx') || file.type.includes('presentation')) return extractPptxText(file);
  if (name.endsWith('.pdf') || file.type === 'application/pdf') return extractPdfText(file);
  throw new Error('Unsupported file — upload a PDF or PPTX deck.');
}

/**
 * Render a PDF's pages to JPEG data-URLs — used to OCR scanned / image-only decks (no text layer),
 * where pdf.js text extraction returns nothing. Bounded page count + width to keep the payload sane.
 */
export async function renderPdfToImages(file: File, maxPages = 24, maxWidth = 2048): Promise<string[]> {
  const pdf = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise;
  const n = Math.min(pdf.numPages, maxPages);
  const out: string[] = [];
  for (let i = 1; i <= n; i++) {
    const page = await pdf.getPage(i);
    const base = page.getViewport({ scale: 1 });
    // Higher DPI so small financial-table digits stay legible to the OCR model.
    const scale = Math.min(3, maxWidth / base.width);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) continue;
    await page.render({ canvasContext: ctx, viewport }).promise;
    out.push(canvas.toDataURL('image/jpeg', 0.85));
    canvas.width = 0; canvas.height = 0; // release the bitmap
  }
  return out;
}

/** True when a PDF's extracted text is too thin to be a real deck (i.e. a scanned image). */
export const isLikelyScanned = (text: string): boolean => text.replace(/\s+/g, '').length < 100;

/** Extract text + page/slide count in one pass — for the Documents-tab upload. */
export async function extractDeckWithMeta(file: File): Promise<{ text: string; pages: number }> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf') || file.type === 'application/pdf') {
    const pdf = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise;
    const parts: string[] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const content = await (await pdf.getPage(i)).getTextContent();
      parts.push(content.items.map((it) => ('str' in it ? it.str : '')).join(' '));
    }
    return { text: parts.join('\n'), pages: pdf.numPages };
  }
  if (name.endsWith('.pptx') || file.type.includes('presentation')) {
    const text = await extractPptxText(file);
    return { text, pages: text.split('\n').filter(Boolean).length };
  }
  throw new Error('Unsupported file — upload a PDF or PPTX deck.');
}
